'use client'
import type { SbBaseBlockProps } from '@/services/storyblok/storyblok'
import { storyblokEditable } from '@storyblok/react'
import { Badge, type BadgeProps, type BadgeVariant } from 'ui/src/components/Badge/Badge'
import { ConditionalWrapper } from 'ui/src/components/ConditionalWrapper'
import { sprinkles } from 'ui/src/theme/sprinkles.css'

const legacyBadgeColorToVariant: Record<string, BadgeVariant> = {
  signalGreenFill: 'green1',
  signalGreenHighlight: 'green3',
  signalBlueFill: 'blue1',
  signalBlueHighlight: 'blue3',
  signalAmberFill: 'amber1',
  signalAmberHighlight: 'amber3',
  signalRedFill: 'red1',
  signalRedHighlight: 'red3',
  signalRedElement: 'redElement',
  gray25: 'grey1',
  grayTranslucent25: 'grey1',
  translucent1: 'grey1',
  gray100: 'grey1',
  surfaceTranslucentPrimary: 'grey1',
  gray200: 'grey2',
  white: 'grey1',
  pinkFill1: 'pink1',
  pinkFill2: 'pink2',
  pinkFill3: 'pink3',
  blueFill1: 'blue1',
  blueFill2: 'blue2',
  blueFill3: 'blue3',
  yellowFill1: 'yellow1',
  green50: 'green1',
  outline: 'outline',
}

export type HeadingLabelBlockProps = SbBaseBlockProps<{
  text: string
  // color holds Storyblok CMS values; may be a legacy color name or a current BadgeVariant
  color: string
  size: BadgeProps['size']
  as: BadgeProps['as']
  alignment?: 'left' | 'center' | 'right'
}>

export const HeadingLabelBlock = ({ blok, nested }: HeadingLabelBlockProps) => {
  const shouldWrap = !nested || Boolean(blok.alignment)
  const variant: BadgeVariant = legacyBadgeColorToVariant[blok.color] ?? (blok.color as BadgeVariant)
  return (
    <ConditionalWrapper
      condition={shouldWrap}
      wrapWith={(children) => (
        <div
          className={sprinkles({
            paddingInline: !nested ? 'md' : undefined,
            textAlign: blok.alignment,
          })}
        >
          {children}
        </div>
      )}
    >
      <Badge as={blok.as} size={blok.size} variant={variant} {...storyblokEditable(blok)}>
        {blok.text}
      </Badge>
    </ConditionalWrapper>
  )
}
